import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { ListingCard } from "@/components/ListingCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PlusCircle, ClipboardList, Loader2 } from "lucide-react";

const STATES = ["vivant", "abattu", "pret-a-cuire"];

export default function BuyerRequests() {
  const { t } = useT();
  const [categories, setCategories] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState(null);
  const [form, setForm] = useState({ category: "poulet-de-chair", state: "", quantity: 10, max_price: "", region: "", delivery_needed: false, needed_by: "", note: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/requests/mine").then((r) => setRequests(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity), max_price: form.max_price ? Number(form.max_price) : null, state: form.state || null, region: form.region || null };
      const { data } = await api.post("/requests", payload);
      toast.success(`${data.matches_count} ${t("request.matchesFound")}`);
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const viewMatches = async (req) => {
    const { data } = await api.get(`/requests/${req.id}/matches`);
    setMatches({ req, listings: data });
  };

  const close = async (id) => { await api.patch(`/requests/${id}/close`); load(); };

  return (
    <div className="container-page py-10">
      <div className="flex items-center justify-between gap-4 mb-6">
        <h1 className="font-heading font-extrabold text-3xl text-secondary">{t("request.myRequests")}</h1>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-request-btn">
          <PlusCircle className="h-4 w-4 mr-2" /> {t("request.create")}
        </Button>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-20 rounded-2xl bg-card border border-dashed border-border" data-testid="requests-empty">
          <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">{t("request.noRequests")}</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {requests.map((r) => (
            <div key={r.id} className="rounded-2xl bg-card border border-border p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`request-${r.id}`}>
              <div>
                <p className="font-heading font-semibold text-secondary flex items-center gap-2">
                  {t(`categories.${r.category}`)}
                  <Badge className={r.status === "open" ? "bg-secondary/10 text-secondary border-0" : "bg-muted text-muted-foreground border-0"}>
                    {r.status === "open" ? t("request.open") : t("request.closed")}
                  </Badge>
                </p>
                <p className="text-sm text-muted-foreground">
                  {r.quantity} · {r.region || "—"} {r.max_price ? `· ≤ ${r.max_price} FCFA` : ""} {r.delivery_needed ? `· ${t("request.deliveryNeeded")}` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => viewMatches(r)} data-testid={`matches-${r.id}`}>{t("request.viewMatches")}</Button>
                {r.status === "open" && <Button variant="ghost" size="sm" onClick={() => close(r.id)} className="text-muted-foreground">{t("request.close")}</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("request.createTitle")}</DialogTitle>
            <DialogDescription>{t("request.sellerSubtitle")}</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t("catalog.category")}</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="req-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{t(`categories.${c.slug}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("catalog.state")}</Label>
              <Select value={form.state || "all"} onValueChange={(v) => set("state", v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="req-state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("catalog.allStates")}</SelectItem>
                  {STATES.map((s) => <SelectItem key={s} value={s}>{t(`states.${s}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("order.quantity")}</Label><Input type="number" min={1} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="mt-1.5" data-testid="req-quantity" /></div>
            <div><Label>{t("request.maxPrice")}</Label><Input type="number" value={form.max_price} onChange={(e) => set("max_price", e.target.value)} className="mt-1.5" data-testid="req-maxprice" /></div>
            <div><Label>{t("catalog.region")}</Label><Input value={form.region} onChange={(e) => set("region", e.target.value)} className="mt-1.5" data-testid="req-region" placeholder="Dakar" /></div>
            <div><Label>{t("request.neededBy")}</Label><Input type="date" value={form.needed_by} onChange={(e) => set("needed_by", e.target.value)} className="mt-1.5" data-testid="req-date" /></div>
          </div>
          <label className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
            <span className="text-sm font-semibold text-secondary">{t("request.deliveryNeeded")}</span>
            <Switch checked={form.delivery_needed} onCheckedChange={(v) => set("delivery_needed", v)} data-testid="req-delivery" />
          </label>
          <Textarea value={form.note} onChange={(e) => set("note", e.target.value)} placeholder={t("request.note")} rows={2} data-testid="req-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={busy} className="bg-primary hover:bg-primary/90" data-testid="req-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("request.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Matches dialog */}
      <Dialog open={!!matches} onOpenChange={() => setMatches(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="matches-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("request.matchingListings")}</DialogTitle>
            <DialogDescription>{matches && t(`categories.${matches.req.category}`)}</DialogDescription>
          </DialogHeader>
          {matches?.listings?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {matches.listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          ) : (
            <p className="py-10 text-center text-muted-foreground">{t("request.noMatches")}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
